class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const isInitialRender = FacetFiltersForm.initialRender;
    const normalizedSearchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    FacetFiltersForm.searchParamsPrev = normalizedSearchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}${
        normalizedSearchParams ? `&${normalizedSearchParams}` : ''
      }`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash && !isInitialRender) FacetFiltersForm.updateURLHash(normalizedSearchParams);
    FacetFiltersForm.initialRender = false;
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    document.getElementById('ProductGridContainer').innerHTML = parsed
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    FacetFiltersForm.updateRelLinks(parsed);
  }

  static renderProductCount(html) {
    const container = document.getElementById('ProductCount');
    if (container) {
      const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.facets-drawer__open', '.facets-drawer__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.facets-drawer__list');
    const sourceFacetsList = source.querySelector('.facets-drawer__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    const currentPage =
      FacetFiltersForm.currentPage ||
      new URLSearchParams(window.location.search).get('page') ||
      '1';
    if (!params.has('page') && currentPage !== '1') {
      params.set('page', currentPage);
    }
    const normalized = params.toString();
    const url = `${window.location.pathname}${normalized ? '?'.concat(normalized) : ''}`;
    history.pushState({ searchParams: normalized }, '', url);
  }

  static patchHistoryMethods() {
    if (FacetFiltersForm.historyPatched) return;
    ['pushState', 'replaceState'].forEach((methodName) => {
      const originalMethod = history[methodName];
      history[methodName] = function (...args) {
        const [state, title, url] = args;
        const normalizedUrl = FacetFiltersForm.normalizeHistoryUrl(url);
        return originalMethod.call(this, state, title, normalizedUrl);
      };
    });
    FacetFiltersForm.historyPatched = true;
  }

  static normalizeHistoryUrl(url) {
    if (!url) return url;
    try {
      const parsedUrl = new URL(url, window.location.href);
      const currentPage = FacetFiltersForm.currentPage || '1';
      if (!parsedUrl.searchParams.has('page') && currentPage !== '1') {
        parsedUrl.searchParams.set('page', FacetFiltersForm.currentPage || '1');
      } else if (parsedUrl.searchParams.get('page') === '1') {
        parsedUrl.searchParams.delete('page');
      }
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (error) {
      return url;
    }
  }

  static syncUrlWithCurrentPage(replace = true) {
    const params = new URLSearchParams(window.location.search);
    const currentPage = FacetFiltersForm.currentPage || '1';
    if (currentPage === '1') {
      params.delete('page');
    } else {
      params.set('page', currentPage);
    }
    const search = params.toString();
    const url = `${window.location.pathname}${search ? '?'.concat(search) : ''}`;
    const method = replace ? history.replaceState : history.pushState;
    method.call(history, { searchParams: search }, '', url);
  }

  static normalizeSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    if (!params.has('page')) {
      const currentPage = FacetFiltersForm.currentPage || '1';
      if (currentPage !== '1') {
        params.set('page', currentPage);
      }
    } else {
      const pageParam = params.get('page') || '1';
      FacetFiltersForm.setCurrentPage(pageParam);
      if (pageParam === '1') {
        params.delete('page');
      }
    }
    return params.toString();
  }

  static setCurrentPage(page) {
    FacetFiltersForm.currentPage = page || '1';
    FacetFiltersForm.updateDocumentTitle();
  }

  static updateDocumentTitle() {
    if (!FacetFiltersForm.documentTitleBase) return;
    const page = FacetFiltersForm.currentPage || '1';
    document.title =
      FacetFiltersForm.documentTitleBase + (page !== '1' ? ` | Page ${page}` : '');
    FacetFiltersForm.updateFirstPageSections();
    FacetFiltersForm.updateRelLinks();
    FacetFiltersForm.updateCanonical();
  }

  static updateRelLinks(sourceDocument = document) {
    const head = document.head;
    const mappings = [
      { rel: 'prev', selector: 'a.pagination__item--next' },
      { rel: 'next', selector: 'a.pagination__item--prev' },
    ];

    mappings.forEach(({ rel, selector }) => {
      const linkHref = sourceDocument.querySelector(selector)?.getAttribute('href');
      let existingLink =
        head.querySelector(`link[rel="${rel}"][data-collection-rel="${rel}"]`) ||
        head.querySelector(`link[rel="${rel}"]`);

      if (!linkHref) {
        if (existingLink) existingLink.remove();
        return;
      }

      if (existingLink && existingLink.dataset.collectionRel === rel) {
        existingLink.remove();
        existingLink = null;
      }

      const link =
        existingLink && !existingLink.dataset.collectionRel
          ? existingLink
          : document.createElement('link');
      link.rel = rel;
      link.href = linkHref;
      link.dataset.collectionRel = rel;
      if (!link.isConnected) head.appendChild(link);
    });
  }

  static updateCanonical() {
    const canonicalLink = document.querySelector('link[rel="canonical"][data-collection-canonical]');
    if (!canonicalLink) return;

    const canonicalBase = canonicalLink.dataset.canonicalBase || canonicalLink.href.split('?')[0];
    canonicalLink.dataset.canonicalBase = canonicalBase;

    const page = Number(FacetFiltersForm.currentPage || '1');
    const newHref = page > 1 && page <= 5 ? `${canonicalBase}?page=${page}` : canonicalBase;

    canonicalLink.setAttribute('href', newHref);
  }

  static cacheFirstPageSections() {
    if (FacetFiltersForm.firstPageSections) return;
    FacetFiltersForm.firstPageSections = Array.from(
      document.querySelectorAll('[data-first-page-section]')
    ).map((section) => ({
      section,
      placeholder: document.createComment('first-page-section-placeholder'),
      parent: section.parentNode,
    }));
  }

  static updateFirstPageSections() {
    FacetFiltersForm.cacheFirstPageSections();
    if (!FacetFiltersForm.firstPageSections) return;
    const showFirstPage = (FacetFiltersForm.currentPage || '1') === '1';

    FacetFiltersForm.firstPageSections.forEach(({ section, placeholder, parent }) => {
      if (!parent) return;
      const isSectionInDom = section.isConnected;
      const isPlaceholderInDom = placeholder.isConnected;

      if (showFirstPage) {
        if (!isSectionInDom) {
          if (isPlaceholderInDom) {
            parent.insertBefore(section, placeholder);
            placeholder.remove();
          } else {
            parent.appendChild(section);
          }
        }
      } else {
        if (isSectionInDom) {
          parent.insertBefore(placeholder, section);
          section.remove();
        }
      }
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static initPagination() {
    document.addEventListener('click', (event) => {
      const paginationLink = event.target.closest('.pagination__item');
      if (!paginationLink) return;
      if (paginationLink.tagName.toLowerCase() !== 'a') return;
      if (!paginationLink.closest('.pagination')) return;
      if (
        (event.button !== undefined && event.button !== 0) ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;

      const url = new URL(paginationLink.href);
      if (url.pathname !== window.location.pathname) return;

      event.preventDefault();
      FacetFiltersForm.scrollToTop();
      FacetFiltersForm.setCurrentPage(url.searchParams.get('page'));
      FacetFiltersForm.syncUrlWithCurrentPage();
      FacetFiltersForm.renderPage(url.search.slice(1), null);
    });
  }

  static scrollToTop() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;
    gridContainer.scrollIntoView({ behavior: 'smooth' });
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.setCurrentPage('1');
    FacetFiltersForm.syncUrlWithCurrentPage();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'facets-drawer__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const forms_sort_mobile = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          } else {
            if (form.id === 'FilterSortByMobile') {
              const noJsElements = document.querySelectorAll('.no-js-list');
              noJsElements.forEach((el) => el.remove());
              forms_sort_mobile.push(this.createSearchParams(form));
            }
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      if (screen.width < 1025 && forms_sort_mobile.length > 0) {
        forms.pop();
        forms.push(forms_sort_mobile[0]);
        this.onSubmitForm(forms.join('&'), event);
      } else {
        this.onSubmitForm(forms.join('&'), event);
      }
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.setCurrentPage('1');
    FacetFiltersForm.syncUrlWithCurrentPage();
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.currentPage = new URLSearchParams(window.location.search).get('page') || '1';
FacetFiltersForm.documentTitleBase = document.title.replace(/\s*\|\s*Page\s+\d+$/gi, '');
FacetFiltersForm.initialRender = true;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.patchHistoryMethods();
FacetFiltersForm.setListeners();
FacetFiltersForm.initPagination();
FacetFiltersForm.updateDocumentTitle();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
